import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Star, Trash2, Edit2 } from 'lucide-react'; // Import Edit2 icon
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

// Interface for the testimonial data
interface Testimonial {
  _id: string;
  name: string;
  image: string;
  description: string;
  createdAt: string;
  rating: number;
  company?: string;
}

const TestimonialSection: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);

  // State for the "Add New" form
  const [newTestimonial, setNewTestimonial] = useState({
    name: '',
    image: '',
    description: '',
    rating: 5,
    company: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // Added selectedFile state

  // NEW: State for the "Edit" form
  // It will hold the testimonial object being edited, or null if no edit is active.
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [selectedEditFile, setSelectedEditFile] = useState<File | null>(null); // Added selectedEditFile state

  // API base URL (use Vite proxy)
  const API_BASE_URL = `${import.meta.env.VITE_API_URL?.replace(/\/$/, "")}/api`;

  // Fetch testimonials from API
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/testimonials`);
      const data = await response.json();

      if (data.success) {
        setTestimonials(data.testimonials);
        setError(null);
      } else {
        setError('Failed to fetch testimonials');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Fetch testimonials error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add new testimonial
  const addTestimonial = async (testimonialData: any) => {
    let finalImageUrl = testimonialData.image;

    // Validation: Ensure either a file is selected or a URL is entered
    if (selectedFile && testimonialData.image) {
      setError('Please either upload a file OR paste a URL, not both.');
      return false;
    }

    if (!selectedFile && !testimonialData.image) {
      setError('You must upload an image or provide an image URL.');
      return false;
    }

    // 1. If a file is selected, upload it first
    if (selectedFile) {
      const uploadFormData = new FormData();
      uploadFormData.append('image', selectedFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/upload`, { // Use /api/upload
          method: 'POST',
          body: uploadFormData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadRes.ok || !uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the URL from the backend
      } catch (err: any) {
        setError(err.message);
        return false; // Stop if upload fails
      }
    }

    // 2. Now, create the testimonial with the final image URL
    try {
      const finalTestimonialData = { ...testimonialData, image: finalImageUrl };
      const response = await fetch(`${API_BASE_URL}/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalTestimonialData),
      });

      const data = await response.json();

      if (data.success) {
        setTestimonials([data.testimonial, ...testimonials]);
        return true;
      } else {
        setError(data.message || 'Failed to add testimonial');
        return false;
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Add testimonial error:', err);
      return false;
    }
  };

  // NEW: Update testimonial function
  const updateTestimonial = async (id: string, testimonialData: Partial<Testimonial>) => {
    let finalImageUrl = testimonialData.image; // Start with the existing image URL

    // 1. Handle new image upload if a file is selected for editing
    if (selectedEditFile) {
      const uploadFormData = new FormData();
      uploadFormData.append('image', selectedEditFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/upload`, { // Use /api/upload
          method: 'POST',
          body: uploadFormData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the NEW URL from the backend
      } catch (err: any) {
        setError(err.message);
        return false; // Stop if the new image upload fails
      }
    }

    // 2. Now, update the testimonial with the final image URL (either the old one or the new one)
    try {
      const finalTestimonialData = { ...testimonialData, image: finalImageUrl };
      const response = await fetch(`${API_BASE_URL}/testimonials/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalTestimonialData),
      });

      const data = await response.json();

      if (data.success) {
        // Update the testimonial in the local state
        setTestimonials(testimonials.map(t =>
          t._id === id ? data.testimonial : t
        ));
        return true;
      } else {
        setError(data.message || 'Failed to update testimonial');
        return false;
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Update testimonial error:', err);
      return false;
    }
  };

  // Delete testimonial
  const deleteTestimonial = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/testimonials/${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setTestimonials(testimonials.filter(testimonial => testimonial._id !== id));
        toast.success('Testimonial deleted');
        return true;
      } else {
        setError(data.message || 'Failed to delete testimonial');
        toast.error(data.message || 'Failed to delete testimonial');
        return false;
      }
    } catch (err) {
      setError('Error connecting to server');
      toast.error('Error deleting testimonial');
      console.error('Delete testimonial error:', err);
      return false;
    }
  };

  // Load testimonials on component mount
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) return 'Today';
    if (diffDays === 2) return '1 day ago';
    if (diffDays <= 30) return `${diffDays - 1} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  };

  // --- Handlers ---
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const success = await addTestimonial(newTestimonial);
    setIsSubmitting(false);

    if (success) {
      setNewTestimonial({ name: '', image: '', description: '', rating: 5, company: '' });
      setSelectedFile(null); // Clear selected file
      setShowAddForm(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this testimonial?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;
    await deleteTestimonial(id);
  };

  // NEW: Handler to open the edit form
  const handleEditClick = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial); // Set the testimonial to be edited
    setSelectedEditFile(null); // Clear any previously selected file for edit
    setShowAddForm(false); // Hide the add form if it's open
  };

  // NEW: Handler for submitting the edit form
  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTestimonial || isSubmitting) return;

    setIsSubmitting(true);
    const success = await updateTestimonial(editingTestimonial._id, editingTestimonial);
    setIsSubmitting(false);

    if (success) {
      setEditingTestimonial(null); // Close the edit form
      setSelectedEditFile(null); // Clear selected edit file
    }
  };


  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-6 h-6 ${index < rating ? 'text-[#E5398D] fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="space-y-8 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="font-poppins text-3xl font-bold text-gray-900">Testimonials</h1>
          <p className="font-inter text-gray-600 mt-1">Client feedback and reviews</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setShowAddForm(true);
            setEditingTestimonial(null); // Ensure edit form is closed
          }}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-primary/90 transition-colors w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span className="font-inter font-medium">Add Testimonial</span>
        </motion.button>
      </motion.div>

      {/* Add Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
        >
          <h3 className="font-poppins text-lg font-semibold mb-4">Add New Testimonial</h3>
          <form onSubmit={handleAddSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Form inputs... */}
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Client Name</label>
              <input type="text" value={newTestimonial.name} onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Image</label>
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  placeholder="Paste Image URL"
                  value={newTestimonial.image}
                  onChange={(e) => {
                    setNewTestimonial({ ...newTestimonial, image: e.target.value });
                    setSelectedFile(null); // Clear selected file if URL is entered
                  }}
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required={!selectedFile} // Required if no file is selected
                />
                <span className="text-gray-500">OR</span>
                <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                  Upload
                  <input
                    type="file"
                    onChange={(e) => {
                      setSelectedFile(e.target.files ? e.target.files[0] : null);
                      setNewTestimonial({ ...newTestimonial, image: '' }); // Clear URL if file is selected
                    }}
                    className="hidden"
                    accept="image/*"
                    required={!newTestimonial.image} // Required if no URL is entered
                  />
                </label>
              </div>
              {selectedFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedFile.name}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Testimonial</label>
              <textarea value={newTestimonial.description} onChange={(e) => setNewTestimonial({ ...newTestimonial, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" rows={4} required />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Rating</label>
              <select value={newTestimonial.rating} onChange={(e) => setNewTestimonial({ ...newTestimonial, rating: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required >
                {[5, 4, 3, 2, 1].map(rating => <option key={rating} value={rating}>{rating} Star{rating > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" disabled={isSubmitting} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Adding...' : 'Add Testimonial'}
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} disabled={isSubmitting} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
            </div>
          </form>
        </motion.div>
      )}

      {/* NEW: Edit Form */}
      {editingTestimonial && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
        >
          <h3 className="font-poppins text-lg font-semibold mb-4">Edit Testimonial</h3>
          <form onSubmit={handleUpdateSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Client Name</label>
              <input type="text" value={editingTestimonial.name} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Image</label>
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  placeholder="Paste Image URL"
                  value={editingTestimonial.image}
                  onChange={(e) => {
                    setEditingTestimonial({ ...editingTestimonial, image: e.target.value });
                    setSelectedEditFile(null); // Clear selected file if URL is entered
                  }}
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required={!selectedEditFile} // Required if no file is selected
                />
                <span className="text-gray-500">OR</span>
                <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                  Upload
                  <input
                    type="file"
                    onChange={(e) => {
                      setSelectedEditFile(e.target.files ? e.target.files[0] : null);
                      setEditingTestimonial({ ...editingTestimonial, image: '' }); // Clear URL if file is selected
                    }}
                    className="hidden"
                    accept="image/*"
                    required={!editingTestimonial.image} // Required if no URL is entered
                  />
                </label>
              </div>
              {selectedEditFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedEditFile.name}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Testimonial</label>
              <textarea value={editingTestimonial.description} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={4} required />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Rating</label>
              <select value={editingTestimonial.rating} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, rating: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required >
                {[5, 4, 3, 2, 1].map(rating => <option key={rating} value={rating}>{rating} Star{rating > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" disabled={isSubmitting} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Updating...' : 'Update Testimonial'}
              </button>
              <button type="button" onClick={() => setEditingTestimonial(null)} disabled={isSubmitting} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
        >
          {error}
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Testimonials Grid */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-20">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-full relative group"
            >
              {/* Edit and Delete Buttons - Hover overlay */}
              <div className="absolute top-4 right-4 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={() => handleEditClick(testimonial)}
                  className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors"
                  aria-label="Edit"
                  title="Edit testimonial"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(testimonial._id)}
                  className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors"
                  aria-label="Delete"
                  title="Delete testimonial"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Speech Bubble */}
              <div
                className="relative bg-[#F3F9FF] rounded-t-xl rounded-br-xl p-6 w-full h-[200px] flex flex-col justify-between"
              >
                {/* Speech bubble tail */}
                <div
                  className="absolute left-0 -bottom-[43px] w-0 h-0 
                    border-t-[43px] border-t-[#F3F9FF]
                    border-l-[21.5px] border-l-transparent
                    border-r-[21.5px] border-r-transparent"
                />

                {/* Testimonial Quote */}
                <p className="font-inter font-semibold text-gray-700 text-sm leading-relaxed">
                  "{testimonial.description}"
                </p>

                {/* Star Rating */}
                <div className="flex items-center">
                  {renderStars(testimonial.rating)}
                </div>
              </div>

              {/* Client Info */}
              <div className="flex items-center space-x-3 mt-6 w-full">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-poppins font-semibold text-gray-800 text-sm">{testimonial.name}</h4>
                  <p className="font-inter text-xs text-gray-500">
                    {getTimeAgo(testimonial.createdAt)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && testimonials.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Star className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="font-poppins text-xl font-semibold text-gray-600 mb-2">No testimonials yet</h3>
          <p className="font-inter text-gray-500">Add your first testimonial to get started!</p>
        </div>
      )}
    </div>
  );
};

export default TestimonialSection;
